import Axios from "../../../lib/Axios";

/**
 * User Moderation API Helper
 * 
 * API functions for user moderation page
 */

/**
 * Get user moderation list
 * @param {Object} filters - Filter parameters
 * @param {string} filters.userId - User ID to search
 * @param {string} filters.keyword - Keyword to search (username)
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @param {string} filters.dateRange - Date range filter (last_24_hours, last_7_days, last_30_days)
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {boolean|string} filters.isSuspended - Suspension status (true, false, or undefined)
 * @returns {Promise} API response with users array and pagination
 */
export const getUserModerationList = async (filters = {}) => {
  const {
    userId,
    keyword,
    limit = 50,
    page = 1,
    dateRange,
    startDate,
    endDate,
    isSuspended,
  } = filters;

  // Build query parameters object
  const params = {
    limit: limit.toString(),
    page: page.toString(),
  };
  
  if (userId && userId.trim() !== "") params.userId = userId.trim();
  if (keyword && keyword.trim() !== "") params.keyword = keyword.trim();
  if (dateRange) params.dateRange = dateRange;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (isSuspended !== undefined && isSuspended !== "") {
    // Convert string "true"/"false" to boolean, or keep boolean as is
    if (isSuspended === "true" || isSuspended === true) {
      params.isSuspended = true;
    } else if (isSuspended === "false" || isSuspended === false) {
      params.isSuspended = false;
    }
  }


  // GET /admin/users
  const result = await Axios.get("admin/users", { params });
  const users = result?.data?.result?.users || [];
  const pagination = result?.data?.result?.pagination || {};
  
  return {
    data: users,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Get user broadcasts
 * @param {Object} filters - Filter parameters
 * @param {string} filters.userId - User ID (required)
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @param {string} filters.status - Status filter (active, expired, pending, rejected)
 * @param {string} filters.dateRange - Date range filter (last_24_hours, last_7_days, last_30_days)
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {boolean|string} filters.isReported - Is reported filter
 * @param {string} filters.titleKeyword - Search by title keyword
 * @param {string} filters.descriptionKeyword - Search by description keyword
 * @returns {Promise} API response with broadcasts array and pagination
 */
export const getUserBroadcasts = async (filters = {}) => {
  const {
    userId,
    limit = 50,
    page = 1,
    status,
    dateRange,
    startDate,
    endDate,
    isReported,
    titleKeyword,
    descriptionKeyword,
  } = filters;

  if (!userId) {
    throw new Error("userId is required");
  }

  // Build query parameters object
  const params = {
    userId: userId,
    limit: limit.toString(),
    page: page.toString(),
  };
  
  if (status) params.status = status;
  if (dateRange && !startDate && !endDate) {
    // Only add dateRange if startDate/endDate are not provided
    params.dateRange = dateRange;
  }
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (isReported !== undefined && isReported !== "") {
    // Convert string "true"/"false" to boolean, or keep boolean as is
    if (isReported === "true" || isReported === true) {
      params.isReported = true;
    } else if (isReported === "false" || isReported === false) {
      params.isReported = false;
    }
  }
  if (titleKeyword) params.titleKeyword = titleKeyword;
  if (descriptionKeyword) params.descriptionKeyword = descriptionKeyword;

  console.log("User Broadcasts API Params:", params);

  // GET /admin/broadcasts
  const result = await Axios.get("admin/broadcasts", { params });
  const broadcasts = result?.data?.result?.broadcasts || [];
  const pagination = result?.data?.result?.pagination || {};
  
  return {
    broadcasts: broadcasts,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

